import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Person } from '../api/types.js';
import { useApiClient } from './use-api-client.js';

/** The people of a server (ADR-030) */
export function usePeople(serverId: string, enabled = true) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['people', serverId],
    queryFn: () => api.people.list(serverId),
    enabled,
    // Refreshed while a reading of GitHub runs, so its result shows when it ends
    refetchInterval: (q) => (q.state.data?.backfill?.status === 'running' ? 5_000 : false),
  });
}

/** Activity per repository over the last months (ADR-030 section 5) */
export function useTeam(serverId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['people', serverId, 'team'], queryFn: () => api.people.team(serverId) });
}

export function usePersonHistory(serverId: string, personId: string, enabled: boolean) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['people', serverId, personId, 'history'],
    queryFn: () => api.people.history(serverId, personId),
    select: (d) => d.history,
    enabled,
  });
}

function useInvalidate(serverId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['people', serverId] });
    void qc.invalidateQueries({ queryKey: ['actors', serverId] });
  };
}

export function useUpdatePerson(serverId: string) {
  const api = useApiClient();
  const invalidate = useInvalidate(serverId);
  return useMutation({
    mutationFn: ({ id, fields }: { id: string; fields: Partial<Pick<Person, 'name' | 'teams' | 'role' | 'growthTarget' | 'leftOn'>> & { priority?: boolean } }) => api.people.update(serverId, id, fields),
    onSettled: invalidate,
  });
}

export function useLinkAccount(serverId: string) {
  const api = useApiClient();
  const invalidate = useInvalidate(serverId);
  return useMutation({ mutationFn: ({ id, ref }: { id: string; ref: string }) => api.people.link(serverId, id, ref), onSettled: invalidate });
}

/** The links aidev proposes (ADR-030 step 6): Link joins the account to the person, Drop forgets the proposal */
export function useLinkProposals(serverId: string) {
  const api = useApiClient();
  const invalidate = useInvalidate(serverId);
  return {
    accept: useMutation({ mutationFn: ({ id, ref }: { id: string; ref: string }) => api.people.acceptLink(serverId, id, ref), onSettled: invalidate }),
    drop: useMutation({ mutationFn: ({ id, ref }: { id: string; ref: string }) => api.people.dropLink(serverId, id, ref), onSettled: invalidate }),
  };
}

export function useReadActivity(serverId: string) {
  const api = useApiClient();
  const invalidate = useInvalidate(serverId);
  return useMutation({ mutationFn: () => api.people.backfill(serverId), onSettled: invalidate });
}

export function useErasePerson(serverId: string) {
  const api = useApiClient();
  const invalidate = useInvalidate(serverId);
  return useMutation({ mutationFn: (id: string) => api.people.erase(serverId, id), onSettled: invalidate });
}

/** The operator's decisions on observations (ADR-030 step 2) */
export function useObservationDecisions(serverId: string) {
  const api = useApiClient();
  const invalidate = useInvalidate(serverId);
  return {
    accept: useMutation({ mutationFn: ({ id, proposalId, text }: { id: string; proposalId: string; text: string }) => api.people.accept(serverId, id, proposalId, text), onSettled: invalidate }),
    drop: useMutation({ mutationFn: ({ id, proposalId }: { id: string; proposalId: string }) => api.people.drop(serverId, id, proposalId), onSettled: invalidate }),
    remove: useMutation({ mutationFn: ({ id, observationId }: { id: string; observationId: string }) => api.people.removeObservation(serverId, id, observationId), onSettled: invalidate }),
  };
}

export function useSeparateAccount(serverId: string) {
  const api = useApiClient();
  const invalidate = useInvalidate(serverId);
  return useMutation({ mutationFn: ({ id, ref }: { id: string; ref: string }) => api.people.separate(serverId, id, ref), onSettled: invalidate });
}

/** The person on the other servers (ADR-030 section 8): read when the row is opened */
export function usePersonIdentity(serverId: string, personId: string, enabled: boolean) {
  const api = useApiClient();
  return useQuery({ queryKey: ['people', serverId, personId, 'identity'], queryFn: () => api.people.identity(serverId, personId), enabled });
}

/** Links and unlinks across servers: every server's list may show it, so all of them are read again */
export function useIdentityDecisions(serverId: string, personId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  const settled = () => { void qc.invalidateQueries({ queryKey: ['people'] }); };
  return {
    link: useMutation({ mutationFn: (other: { serverId: string; personId: string }) => api.people.linkIdentity(serverId, personId, other), onSettled: settled }),
    unlink: useMutation({ mutationFn: () => api.people.unlinkIdentity(serverId, personId), onSettled: settled }),
  };
}
